import { faClose } from "@fortawesome/pro-regular-svg-icons";
import { faArrowRight } from "@fortawesome/pro-solid-svg-icons";
import { Button, Stack } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useEffect, useRef } from "react";
import { useEventCallback, useLocalStorage } from "usehooks-ts";

import { Icon, IconButton, Typography } from "@/components/ui-v2";
import { useBasicData, useTrackNextAppSwitch } from "@/hooks";
import { getWebAppLoginUrl } from "@/utils/env";
import { capturePostHogEvent } from "@/utils/posthog";

export const NEW_LAYER_BANNER_DISMISSED_KEY = "layer.nextAppBannerDismissed";
/** Fixed banner height (px). The app shell offsets the nav Drawer by this. */
export const NEW_LAYER_BANNER_HEIGHT = 44;
/** Below this viewport width (px) the "I'm not interested" label is hidden. */
const DISMISS_LABEL_MIN_WIDTH = 767;

const Root = styled(Stack)(({ theme }) => ({
  flexDirection: "row",
  alignItems: "center",
  gap: theme.spacing(4),
  height: NEW_LAYER_BANNER_HEIGHT,
  flexShrink: 0,
  padding: theme.spacing(0, 4),
  background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, #7B2FF7 50%, ${theme.palette.primary.main} 100%)`,
  color: theme.palette.common.white,
}));

export const NewLayerBanner = () => {
  const [isDismissed, setIsDismissed] = useLocalStorage(
    NEW_LAYER_BANNER_DISMISSED_KEY,
    false,
  );
  const { user } = useBasicData();
  const trackNextAppSwitch = useTrackNextAppSwitch();

  const userId = user?.id;
  const hasTrackedShown = useRef(false);
  useEffect(() => {
    if (isDismissed || !userId || hasTrackedShown.current) return;
    hasTrackedShown.current = true;
    capturePostHogEvent("next_app.banner_shown", userId);
  }, [isDismissed, userId]);

  const onDismiss = useEventCallback(() => {
    if (userId) {
      capturePostHogEvent("next_app.banner_dismissed", userId);
    }
    setIsDismissed(true);
  });

  const onTryNextApp = useEventCallback(() => {
    trackNextAppSwitch("banner");
    window.location.href = getWebAppLoginUrl();
  });

  if (isDismissed) return null;

  return (
    <Root>
      <Stack
        direction="row"
        sx={{ alignItems: "center", gap: 4, flex: 1, minWidth: 0 }}
      >
        <Typography variant="small" sx={{ color: "common.white" }}>
          <Typography component="span" variant="small" sx={{ fontWeight: 700 }}>
            The new Layer is here.
          </Typography>{" "}
          Rebuilt from the ground up — build any creatives with a single prompt.
        </Typography>
        <Button
          onClick={onTryNextApp}
          variant="contained"
          color="secondary"
          size="medium"
          rounded
          endIcon={<Icon icon={faArrowRight} />}
        >
          Try it now
        </Button>
      </Stack>
      <Stack
        direction="row"
        sx={{ alignItems: "center", gap: 2, flexShrink: 0 }}
      >
        <Button
          variant="text"
          color="inherit"
          size="small"
          onClick={onDismiss}
          sx={(theme) => ({
            display: "none",
            [theme.breakpoints.up(DISMISS_LABEL_MIN_WIDTH)]: {
              display: "inline-flex",
            },
          })}
        >
          I&apos;m not interested
        </Button>
        <IconButton
          aria-label="Dismiss banner"
          variant="quaternary"
          onClick={onDismiss}
          sx={{ color: "common.white" }}
        >
          <Icon icon={faClose} />
        </IconButton>
      </Stack>
    </Root>
  );
};
